import React, { useEffect, useState, useRef } from "react";
import { Box, Button, IconButton, Tooltip } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import styles from './BloodTestResults.module.css';
import * as apiVitals from '../../services/ApiVitals';
import Loader from "../../components/Loader";
import tabBloodPrNormal from '../../assets/images/tab_blood_pressure_normal.svg';
import { notifyError, notifyInfo } from "../../components/NotifyToast";
import { Bar } from 'react-chartjs-2';
import { MdOutlineSummarize } from "react-icons/md";
import { FaFireAlt, FaRegHeart } from "react-icons/fa";
import { GiWeight, GiFat, GiNightSleep } from "react-icons/gi";
import { PiSmileySad } from "react-icons/pi";
import { SiOxygen } from "react-icons/si";
import { IoArrowBackOutline } from "react-icons/io5";
import { config } from '../../config.env';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    TimeScale,
    BarElement,
    Title,
    Tooltip as ChartTooltip,
    Legend,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';

ChartJS.register(
    CategoryScale,
    LinearScale,
    TimeScale,
    BarElement,
    Title,
    ChartTooltip,
    Legend,
    ChartDataLabels
);

const endpoint = config.ENDPOINT;

function bmrCalculate(isMale, kg, cm, years) {
    return isMale
        ? 88.362 + (13.397 * kg) + (4.799 * cm) - (5.677 * years)
        : 447.593 + (9.247 * kg) + (3.098 * cm) - (4.33 * years);
}

const chartInfo = {
    summary: {
        title: "DAILY SUMMARY",
        icon: <MdOutlineSummarize size='1.2em' />,
        apiField: "KCAL_BURNED",
        graphTitle: "Daily Calories Burned (kcal)",
        graphKey: "",
        graphMax: 3000,
        graphIcon: <FaFireAlt size='1.2em' />,
        grader: (burned, bmr = 1800) => {
            let kcalGrade;
            if (burned < bmr)
                kcalGrade = (1 - ((bmr - burned) / 1000)) / 1.5;
            else
                kcalGrade = Math.min(1 - ((burned - bmr - 500) / 1000), 1);
            return Math.max(0, kcalGrade);
        }
    },
    body: {
        title: "WEIGHT",
        icon: <GiWeight size='1.2em' />,
        apiField: "BODY_FAT",
        graphTitle: "Daily Body Fat (%)",
        graphKey: "",
        graphMax: 50,
        graphIcon: <GiFat size='1.2em' />,
        grader: (fat, gender = "male") => {
            const fatBest = gender === "male" ? 16 : 28.5;
            return Math.max(0, Math.min(1 - (Math.abs(fatBest - fat) / 18), 1));
        }
    },
    heart: {
        title: "HEART RATE",
        icon: <FaRegHeart size='0.9em' />,
        apiField: "RESTING_HEART_RATE",
        graphTitle: "Daily Resting Pulse Rate (BPM)",
        graphKey: "restBpm",
        graphMax: 135,
        graphIcon: <FaRegHeart size='0.9em' />,
        grader: value => Math.max(0, Math.min(1 - ((value - 60) / 40), 1))
    },
    pressure: {
        title: "BLOOD PRESSURE",
        icon: <FaRegHeart size='0.8em' />,
        apiField: "BLOODPRESSURE",
        graphTitle: "Daily Blood Pressure Level (mmHg)",
        graphKey: "BLOOD_PRESSURE_SYSTOLIC",
        graphKeyB: "BLOOD_PRESSURE_DIASTOLIC",
        graphMax: 210,
        graphIcon: <FaRegHeart size='0.9em' />,
        grader: value => Math.max(0, Math.min(1 - Math.abs(value - 105) / 35, 1)),
        graderB: value => Math.max(0, Math.min(1 - Math.abs(value - 70) / 20, 1))
    },
    sleep: {
        title: "SLEEP",
        icon: <GiNightSleep size='1.2em' />,
        apiField: "SLEEP_SCORE",
        graphTitle: "Daily Sleep Score",
        graphKey: "",
        graphMax: 100,
        graphIcon: <GiNightSleep size='1.2em' />,
        grader: value => Math.max(0, (value - 50) / 50)
    },
    stress: {
        title: "STRESS",
        icon: <PiSmileySad size='1.2em' />,
        apiField: "STRESS_SCORE",
        graphTitle: "Daily Average Stress Score",
        graphKey: "",
        graphMax: 100,
        graphIcon: <PiSmileySad size='1.2em' />,
        grader: value => Math.max(0, Math.min(1 - ((value - 15) / 65), 1))
    },
    spo2: {
        title: "OXYGEN LEVEL",
        icon: <SiOxygen size='0.9em' />,
        apiField: "BLOOD_OXYGEN_SATURATION",
        graphTitle: "Daily Average SpO2 (%)",
        graphKey: "spo2",
        graphMax: 100,
        graphIcon: <SiOxygen size='0.9em' />,
        grader: value => Math.min(Math.max(0, (value - 80) / 20), 1)
    }
};

function CardItem({ type, info, onClickCard }) {
    if (!info) return null;
    const { title, icon, apiField } = chartInfo[type];
    const grade = info.grade;
    const clickable = apiField !== '';
    let scale = 1;
    let barColor = '#E1E3E9';
    if (grade <= 1) {
        if (grade <= 0.4) scale = ((1 - grade) * 0.05) + 1;
        barColor = `hsl(${(grade * 120)},80%,50%)`;
    }
    return (
        <Box
            onClick={() => clickable && onClickCard(type)}
            sx={{
                cursor: clickable ? 'pointer' : 'auto',
                display: 'grid',
                alignItems: 'start',
                padding: '1em',
                height: '48vw',
                width: '34vw',
                border: grade <= 0.5 ? scale * 2 : 1,
                borderRadius: '0.5em',
                borderColor: grade <= 0.5 ? barColor : '#E1E3E9',
                bgcolor: '#FFF',
                borderBottom: grade <= 1 ? 8 : 1,
                borderBottomColor: barColor,
            }}
        >
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                marginBottom: 1,
                color: '#082C91',
                fontWeight: 700,
                fontSize: '0.9em'
            }}>
                {title}&nbsp;{icon}
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', fontSize: '0.8em' }}>
                {type === "summary" && (
                    <div>
                        <div><pre>Calories Burned  <b>{info.kcal} kcal</b></pre></div>
                        <div><pre>Active Time  <b>{Math.floor(info.active / 60)} h {info.active % 60} min</b></pre></div>
                        <div><pre>Steps  <b>{info.steps}</b></pre></div>
                        <div><pre>Distance  <b>{info.dist} m</b></pre></div>
                    </div>
                )}
                {type === "body" && (
                    <>
                        <div><pre>Weight  <b>{info.weight} kg</b></pre></div>
                        <div><pre>Muscle Mass  <b>{info.muscle} kg</b></pre></div>
                        <div><pre>Body Fat  <b>{info.fat} %</b></pre></div>
                    </>
                )}
                {type === "heart" && (
                    <>
                        <div>Daily Average Pulse<br /><b>{info.avg} BPM</b><br /><br /></div>
                        <div>Resting Rate<br /><b>{info.rest} BPM</b></div>
                    </>
                )}
                {type === "pressure" && (
                    <>
                        <div>Systolic Level<br /><b>{info.sys} mm Hg</b><br /><br /></div>
                        <div>Diastolic Level<br /><b>{info.dia} mm Hg</b></div>
                    </>
                )}
                {type === "sleep" && (
                    <>
                        <div>Daily Sleep Score<br /><b>{info.score} / 100</b><br /><br /></div>
                        <div>Total Time<br /><b>{Math.floor(info.total / 60)} h {info.total % 60} min</b></div>
                    </>
                )}
                {type === "stress" && (
                    <div>Daily Stress Score<br /><b>{info.avg} / 100</b></div>
                )}
                {type === "spo2" && (
                    <div>Daily Average SpO2<br /><b>{info.avg} %</b></div>
                )}
            </Box>
            <Box sx={{
                justifySelf: 'end',
                alignSelf: 'end',
                fontSize: '0.5em',
                color: 'gray',
            }}>
                {new Date(info.date).toDateString()}
            </Box>
        </Box>
    );
}

function CardPage({ cardsInfo, onClickCard }) {
    return (
        <>
            <Box sx={{
                border: 1,
                borderRadius: '0.75em',
                borderColor: '#E1E3E9',
                bgcolor: '#FFF',
                mx: 1,
                '@media (orientation: portrait)': { fontSize: '0.5em' }
            }}>
                <Box sx={{
                    color: '#082C91',
                    fontWeight: 700,
                    backgroundColor: '#E3E6F2',
                    borderBottom: '1px solid #E1E3E9',
                    borderRadius: '0.5em',
                    padding: '0.5em 1em'
                }}>Data Information</Box>
                <Box sx={{
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr 1fr',
                    gridGap: '20px',
                    padding: '20px',
                    '@media (orientation: portrait)': {
                        gridTemplateColumns: '1fr',
                        gridGap: '0.75em',
                        padding: '0.75em',
                    }
                }}>
                    <div>
                        <div style={{ fontWeight: 700, color: '#082C91' }}>Device Model</div>
                        <div>{!cardsInfo ? "-" : cardsInfo.device.device}</div>
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, color: '#082C91' }}>Device Firmware</div>
                        <div>{!cardsInfo?.device?.firmware ? "-" : cardsInfo.device.firmware}</div>
                    </div>
                    <div>
                        <div style={{ fontWeight: 700, color: '#082C91' }}>Date</div>
                        <div>{!cardsInfo?.date ? "-" : cardsInfo.date}</div>
                    </div>
                </Box>
            </Box>
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                flexWrap: 'wrap',
                color: '#082C91',
                fontSize: '20px',
                mb: 2,
                '@media (orientation: portrait)': {
                    flexDirection: 'column',
                    alignContent: 'center',
                    fontSize: '4vw',
                }
            }}>
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'start',
                    alignItems: 'center',
                    overflow: "hidden",
                    mt: 2, mx: 2,
                }}>
                    {cardsInfo === null
                        ? <div style={{ textAlign: 'center', margin: '20%', fontSize: 28 }}>ไม่พบข้อมูล</div>
                        : <Box sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            alignContent: 'space-between',
                            gridGap: '1em',
                        }}>
                            {["summary", "body", "heart", "pressure", "sleep", "stress", "spo2"].map(type =>
                                <CardItem key={type} type={type} info={cardsInfo[type]} onClickCard={onClickCard} />
                            )}
                        </Box>
                    }
                </Box>
            </Box>
        </>
    );
}

function GraphPage({ chartRef, barOptions, barData, dateRange, limitRange, onClickRange }) {
    return (
        <Box sx={{
            display: 'block',
            fontSize: '1em',
            mt: { xs: 1, sm: 3, md: 5 },
            mx: { xs: 1, sm: 2 },
            color: '#082C91',
        }}>
            {/* Range Buttons */}
            <Box sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                mt: 3, mb: 2, mx: 3,
                gap: 1,
                 '@media (orientation: portrait)': {
                    justifyContent: 'center',
                    mt: 0
                }
            }}>
                {limitRange >= 1 ? (
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                            xs: 'repeat(auto-fit, minmax(100px, 1fr))',
                            sm: `repeat(${1 + limitRange}, 1fr)`
                        },
                        gap: 1,
                        width: { xs: '100%', lg: '60%' },
                        
                    }}>
                        <Button onClick={() => onClickRange(0)} variant={dateRange === 0 ? 'contained' : 'outlined'} size="small">1 Week</Button>
                        <Button onClick={() => onClickRange(1)} variant={dateRange === 1 ? 'contained' : 'outlined'} size="small">2 Weeks</Button>
                        {limitRange >= 2 && <Button onClick={() => onClickRange(2)} variant={dateRange === 2 ? 'contained' : 'outlined'} size="small">1 Month</Button>}
                        {limitRange >= 3 && <Button onClick={() => onClickRange(3)} variant={dateRange === 3 ? 'contained' : 'outlined'} size="small">3 Months</Button>}
                    </Box>
                ) : (
                    <Box sx={{ fontSize: 'small', alignContent: 'center' }}><i>[ 1 Week ]</i></Box>
                )}
            </Box>

            {/* Chart Container */}
            <Box sx={{
                p: 1,
                mt: 2,
                borderRadius: '13px',
                bgcolor: '#E3E9F2',
                //minHeight: '50vh',
                //height: { xs: '60vh', md: '65vh' },
                maxHeight: '75vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
                <Box sx={{
                    width: '100%',
                    maxWidth: '1000px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: 2,
                    py: 2,
                    borderRadius: '13px',
                    bgcolor: '#FFFFFF',
                    overflow: 'hidden',
                }}>
                    {/* Chart */}
                    <Box sx={{
                        width: '100%',
                        px: { xs: 1, sm: 2 },
                    }}>
                        <Bar
                            ref={chartRef}
                            options={barOptions}
                            data={barData}
                            style={{ width: '100%', height: '100%' }}
                        />
                    </Box>

                    {/* Legend */}
                    {limitRange >= 1 && (
                        <Box sx={{ width: 'auto', maxWidth: '90%', textAlign: 'center' }}>
                            <img
                                className={styles.Img}
                                src={tabBloodPrNormal}
                                alt='color legend'
                                style={{ maxWidth: '100%', height: 'auto' }}
                            />
                        </Box>
                    )}
                </Box>
            </Box>
        </Box>
    );
}


export default function SmartWatch() {
    const navigate = useNavigate();
    const PID = useSelector((state) => state.auth.user_data.pid);
    const hwRegistered = true;
    const [loading, setLoading] = useState(false);
    const [cardsInfo, setCardsInfo] = useState(null);
    const [graphPage, setGraphPage] = useState('');
    const fetchedData = useRef(null);
    const chartRef = useRef();
    const [barOptions, setBarOptions] = useState(null);
    const [barData, setBarData] = useState(null);
    const [limitRange, setLimitRange] = useState(3);
    const prefDateRange = useRef(0);
    const dateRangeRef = useRef(0);
    const [dateRange, setDateRangeState] = useState(0);
    const setDateRange = (value) => {
        dateRangeRef.current = value;
        setDateRangeState(value);
    };

    const backButton = () => {
        if (graphPage === '' || barOptions === null || barData === null)
            navigate('/mainMenu');
        else
        {
            setGraphPage('');
            setBarOptions(null);
            setBarData(null);
        }
    }

    const onClickRange = (range) => {
        prefDateRange.current = range;
        setDateRange(range);
    };

    const chartReset = () => {
        setBarOptions({
            plugins: {
                legend: { display: false },
                tooltip: { enabled: false },
                datalabels: {}
            },
            scales: {
                y: {
                    beginAtZero: true,
                    border: { display: false },
                    ticks: { font: { size: '12%' } },
                },
                x: { ticks: { display: false } }
            }
        });
        setBarData({ labels: [], datasets: [] });
    };

    useEffect(() => {
        window.addEventListener("resize", handleResize);
        handleResize();
        return () => { window.removeEventListener("resize", handleResize); };
        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (hwRegistered) getSmartWatch();
        // eslint-disable-next-line
    }, [hwRegistered]);

    useEffect(() => {
        if (graphPage === '') return;
        if (cardsInfo == null) {
            chartReset();
            setGraphPage('');
            return;
        }
        const field = chartInfo[graphPage].apiField;
        getSmartWatchGraph(field);
        // eslint-disable-next-line
    }, [cardsInfo, graphPage, dateRange]);

    const getSmartWatch = async () => {
        setLoading(true);
        let res = null; // await apiVitals.getSmartWatch();
        setLoading(false);
        fetchedData.current = {};
        setCardsInfo(null);

        // MockUp
        if ((!res || res['data']['code'] !== 200) && !config.PRODUCTION)
        {
            console.log("Mockup SmartWatch");
            res = {
                data: {
                    code: 200,
                    data: {
                        huawei_health: {
                            device: {
                                type: 'Huawei Watch D2',
                                firmware_version: 'HSD2-V1.0.24.8'
                            },
                            user: {
                                gender: 'male',
                                age: 28,
                                weight: 79,
                                height: 177,
                            },
                            date: '2025-03-25',
                            daily_summary: {
                                active_time: 1200000,
                                steps: 548,
                                distance: 1860,
                            },
                            body_metrics: [
                                {
                                    measurement_time: '2025-03-25T22:02:33Z',
                                    bmi: 25,
                                    weight: 76,
                                    muscle_mass: 46,
                                    body_fat_percentage: 23
                                }
                            ],
                            heart_rate: {
                                daily_average: 71,
                                result: [
                                    { timestamp: '2025-03-25T22:02:11Z' }
                                ]
                            },
                            sleep: {
                                end_time: '2025-03-07:10:34Z',
                                sleep_score: 81,
                                total_duration: 27000000
                            },
                            stress: {
                                daily_average: 45,
                                result: [
                                    { timestamp: '2025-03-25T20:27:17Z' }
                                ]
                            },
                            spo2: {
                                daily_average: 98,
                                result: [
                                    { timestamp: '2025-03-25T17:44:28Z' }
                                ]
                            }
                        }
                    }
                }
            };
        }

        if (!res) notifyError('Fetch Error');
        else if (res['data']['code'] !== 200) notifyInfo(res['data']['message']);
        else {
            const data = res['data']['data']['huawei_health'];
            if (data && data.device?.type && data.user) {
                const { gender, age, weight, height } = data.user;
                const bmr = bmrCalculate(gender === "male", weight, height, age);
                const kcal = data.daily_summary.calories_burned;
                const fat = data.body_metrics[0].body_fat_percentage;
                const pulseRest = data.heart_rate.resting_heart_rate;
                const sys = Math.round((0.75 + Math.random() / 2) * 105);
                const dia = Math.round((0.75 + Math.random() / 2) * 70);
                const gradeSys = chartInfo.pressure.grader(sys);
                const gradeDia = chartInfo.pressure.graderB(dia);

                setCardsInfo({
                    date: data.date,
                    device: {
                        device: data.device.type,
                        firmware: data.device.firmware_version,
                    },
                    user: { age, gender, weight, height },
                    summary: data.daily_summary && {
                        date: Date.parse(data.date),
                        kcal,
                        active: data.daily_summary.active_time / 60000,
                        steps: data.daily_summary.steps,
                        dist: data.daily_summary.distance,
                        grade: chartInfo.summary.grader(kcal, bmr),
                    },
                    body: data.body_metrics && {
                        date: Date.parse(data.body_metrics[0].measurement_time),
                        bmi: data.body_metrics[0].bmi,
                        weight: data.body_metrics[0].weight,
                        muscle: data.body_metrics[0].muscle_mass,
                        fat,
                        grade: chartInfo.body.grader(fat, gender),
                    },
                    heart: data.heart_rate && {
                        date: Date.parse(data.heart_rate.result[0].timestamp),
                        avg: data.heart_rate.daily_average,
                        rest: pulseRest,
                        grade: chartInfo.heart.grader(pulseRest),
                    },
                    pressure: {
                        date: Date.parse(data.heart_rate.result[0].timestamp),
                        sys,
                        dia,
                        grade: Math.min(gradeSys, gradeDia),
                        gradeA: gradeSys,
                        gradeB: gradeDia,
                    },
                    sleep: data.sleep && {
                        date: Date.parse(data.sleep.end_time),
                        score: data.sleep.sleep_score,
                        total: data.sleep.total_duration / 60000,
                        grade: chartInfo.sleep.grader(data.sleep.sleep_score),
                    },
                    stress: data.stress && {
                        date: Date.parse(data.stress.result[0].timestamp),
                        avg: data.stress.daily_average,
                        grade: chartInfo.stress.grader(data.stress.daily_average),
                    },
                    spo2: data.spo2 && {
                        date: Date.parse(data.spo2.result[0].timestamp),
                        avg: data.spo2.daily_average,
                        grade: chartInfo.spo2.grader(data.spo2.daily_average),
                    },
                });
            }
        }
    };

    const handleResize = () => {
        const w = window.innerWidth;
        const limit = Math.min(Math.floor(w / 512), 3);
        setLimitRange(limit);
        let largeSpan = false;
        if (dateRangeRef.current > limit) {
            setDateRange(limit);
            largeSpan = (limit > 2);
        } else if (prefDateRange.current <= limit && dateRangeRef.current !== prefDateRange.current) {
            setDateRange(prefDateRange.current);
            largeSpan = (prefDateRange.current > 2);
        }
        if (barOptions && barData) {
            const xOptions = barOptions.scales.x;
            const gplugins = barOptions.plugins;
            const ticksDisplay = (barData.labels.length <= 7 || w >= 512);
            if (xOptions.ticks.display !== ticksDisplay) {
                const sampledLength = fetchedData.current?.values[0]?.toString().length || 0;
                gplugins.datalabels.formatter = (value) => {
                    if (largeSpan || w < 512) {
                        if (value >= 1000)
                            value = (value / 1000).toFixed(1) + 'k';
                        else if (sampledLength >= 4)
                            value = Math.round(value);
                    }
                    return value;
                };
                xOptions.ticks.display = ticksDisplay;
                setBarOptions({
                    plugins: gplugins,
                    scales: {
                        y: barOptions.scales.y,
                        x: xOptions
                    }
                });
            }
        }
    };

    const getSmartWatchGraph = async (field) => {
        const populate = false;
        let doMockUp = false;
        let range = dateRange;
        if (populate) {
            doMockUp = true;
            range = 3;
        }
        const timeNow = Math.floor(Date.now() / 86400000) * 86400000;
        const rangeMs = 86400000 * (range <= 1 ? (range === 0 ? 6 : 13) : (range === 2 ? 30 : 92));
        let date_from = timeNow - rangeMs;
        setLoading(true);
        let res = null; // await apiVitals.getSmartWatchRecords(field, new Date(date_from).toISOString().slice(0,10), new Date(date_to).toISOString().slice(0,10));
        setLoading(false);
        fetchedData.current = null;
        let fetched;
        let dataCount = 0;

        if (!res) notifyError('Fetch Error');
        else if (res['data']['code'] !== 200) notifyInfo(res['data']['message']);
        else {
            fetchedData.current = { labels: [], values: [], colors: [] };
            fetched = fetchedData.current;
            if (field === "BLOODPRESSURE") {
                fetched.valuesB = [];
                fetched.colorsB = [];
            }
            const recordList = res['data']['data']['huawei_health_record_list'];
            dataCount = recordList.length;

            // Mock Up Populate
            if (doMockUp) {
                const key = chartInfo[graphPage].graphKey;
                const keyB = field === "BLOODPRESSURE" ? chartInfo[graphPage].graphKeyB : false;
                if (dataCount <= 0) {
                    recordList.push({
                        'date': new Date(date_from).toISOString().slice(0, 10),
                        [key]: Math.round((chartInfo[graphPage].graphMax / 2) * (0.5 + Math.random())),
                    });
                    dataCount++;
                }
                const today = Date.now();
                let currDate = new Date(recordList[dataCount - 1].date).getTime() + Math.round(Math.random() * 7 * 86400000);
                const firstValue = recordList[dataCount - 1][key];
                while (currDate < today) {
                    const data = {
                        'date': new Date(currDate).toISOString().slice(0, 10),
                        [key]: Math.round(firstValue * (0.8 + (Math.random() * 0.3))),
                    };
                    if (keyB)
                        data[keyB] = Math.round((data[key] - 70 + (Math.random() * 40)));
                    recordList.push(data);
                    dataCount++;
                    currDate += Math.round(Math.random() * 7 * 86400000);
                }
            }
            const key = chartInfo[graphPage].graphKey;
            const grader = chartInfo[graphPage].grader;
            const keyB = field === "BLOODPRESSURE" ? chartInfo[graphPage].graphKeyB : false;
            const graderB = keyB ? chartInfo[graphPage].graderB : undefined;
            recordList.slice().reverse().forEach((record) => {
                const date = record.date;
                const value = record[key];
                fetched.labels.unshift(date);
                fetched.values.unshift(value);
                fetched.colors.unshift(`hsl(${Math.round(grader(value) * 120)},80%,50%,0.75)`);
                if (keyB) {
                    const valueB = record[keyB];
                    fetched.valuesB.unshift(valueB);
                    fetched.colorsB.unshift(`hsl(${Math.round(graderB(valueB, true) * 120)},80%,40%,1)`);
                }
            });
        }
        if (chartRef.current) {
            chartRef.current.data = [];
            chartRef.current.update('none');
        }
        if (dataCount <= 0) chartReset();
        else {
            const sampledLength = fetched.values[0].toString().length;
            const xSize = 16 - (dataCount / 15);
            const labelSize = xSize - Math.round(sampledLength / 3);
            const doFormat = (dataCount > 30 || limitRange < 1);
            setBarOptions({
                plugins: {
                    legend: { display: false },
                    tooltip: { enabled: false },
                    datalabels: {
                        anchor: 'end',
                        align: ctx => ctx.datasetIndex === 0 ? 'end' : 'start',
                        offset: ctx => ctx.datasetIndex === 0 ? -5 : 5,
                        textStrokeWidth: ctx => ctx.datasetIndex === 0 ? 0 : 4,
                        textStrokeColor: '#FFF',
                        font: { size: labelSize + '%' },
                        formatter: value => {
                            if (doFormat) {
                                if (value >= 1000)
                                    value = (value / 1000).toFixed(1) + 'k';
                                else if (sampledLength >= 4)
                                    value = Math.round(value);
                            }
                            return value;
                        }
                    }
                },
                scales: {
                    y: {
                        beginAtZero: true,
                        suggestedMax: chartInfo[graphPage].graphMax,
                        border: { display: false },
                        ticks: { font: { size: '16%' } },
                    },
                    x: {
                        border: { display: false },
                        grid: { display: false },
                        ticks: { display: (dataCount <= 7 || limitRange >= 1), font: { size: xSize + '%' } }
                    }
                }
            });
            const thisBarData = {
                labels: fetched.labels,
                datasets: [{
                    label: '',
                    data: fetched.values,
                    backgroundColor: fetched.colors,
                    grouped: false,
                }]
            };
            if (graphPage === "pressure") {
                thisBarData.datasets.push({
                    label: "dia",
                    data: fetched.valuesB,
                    backgroundColor: fetched.colorsB,
                });
            }
            setBarData({
                labels: thisBarData.labels,
                datasets: [...thisBarData.datasets]
            });
        }
    };

    return (
        <Box sx={{
            color: '#263238',
            display: 'flex',
            flexDirection: 'column',
            gridGap: 20,
            fontSize: 18,
            margin: { xl: '2rem', lg: '2rem', md: '2rem', sm: '2rem', xs: '2rem 1rem' },
            '@media (max-width: 1023px)': { gridGap: '3%', fontSize: 16 },
            '@media (max-width: 800px)': { gridGap: '1%', fontSize: 14 },
            '@media (orientation: portrait)': { fontSize: '6vw' }
        }}>
            {loading && <Loader loading={loading} />}
            <Box sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1fr',
                mb: 2,
                '@media (orientation: portrait)': {
                    gridTemplateColumns: '0.5fr 4fr 0.5fr',
                    '@media (min-width: 1000px)': { m: '1em' },
                }
            }}>
                <Box sx={{
                    display: 'flex',
                    justifyContent: 'start',
                    ml: '3rem',
                    '@media (orientation: portrait)': { ml: 0 }
                }}>
                    <Tooltip title="ย้อนกลับ">
                        <IconButton onClick={backButton} aria-label="Back" color="c_button" variant="resp">
                            <IoArrowBackOutline size='1.25em' />
                        </IconButton>
                    </Tooltip>
                </Box>
                <Box sx={{
                    textAlign: 'center',
                    alignSelf: 'end',
                    color: 'c_title.main',
                    '@media (orientation: portrait)': { alignSelf: 'center' }
                }}>
                    {(graphPage === '' || barOptions === null || barData === null) ?
                        "ข้อมูลจาก SmartWatch"
                        :
                        <Box sx={{ display: 'inline', alignContent: 'center', '@media (max-width: 512px)': { fontSize: '1em' } }}>
                            {chartInfo[graphPage].graphTitle} &nbsp; {chartInfo[graphPage].graphIcon}
                        </Box>
                    }
                </Box>
            </Box>
            {hwRegistered ?
                ((graphPage === '' || barOptions === null || barData === null) ?
                    <CardPage cardsInfo={cardsInfo} onClickCard={setGraphPage} />
                    :
                    <GraphPage {...{chartRef, barOptions, barData, dateRange, limitRange, onClickRange }} />
                )
                :
                <div style={{ textAlign: 'center', margin: '20%', fontSize: 28 }}>
                    <Box sx={{ marginBottom: 2 }}><b>Huawei ID Unregistered</b></Box>
                    <Button
                        sx={{ fontSize: 18 }}
                        onClick={() => {
                            const newWindow = window.open(`${endpoint}/huawei/auth/huawei?pid=${PID}`, '_blank', 'noopener,noreferrer');
                            if (newWindow) newWindow.opener = null;
                        }}
                        variant={'outlined'}
                    >
                        Register Now
                    </Button>
                </div>
            }
        </Box>
    );
}
